import type { PingKind } from '@il-colpo/engine';
import { useKeyActions, type KeyHandlers } from '../systems/input/keybindings.ts';
import { useGame } from './GameContext.tsx';
import { usePadStore } from './padStore.ts';

/** Quick phrases that need no parameters, in the order of the number keys 1–7. */
const QUICK_PHRASES = ['ready', 'wait', 'go', 'danger', 'done', 'repeat', 'dontKnow'] as const;
/** Keys 8 and 9 raise a ping instead. */
const QUICK_PINGS: readonly PingKind[] = ['danger', 'go'];

/** Where the plan is on screen: the blueprint while planning, the route strip during the job. */
export const PLAN_VIEW_ID = 'plan-view';

/**
 * The game's keyboard actions (settings, Controls): quick phrases on the number keys, a ping
 * on the ping key, the phrase pad, a jump to the plan, and the pause. The keys themselves are
 * the player's to rebind; this only says what each action does.
 */
export function useGameKeys(onPause: () => void): void {
  const { view, say, send } = useGame();
  const toggleChat = usePadStore((s) => s.toggle);
  const phase = view.game.phase;
  const talking =
    phase === 'planning' || phase === 'execution' || phase === 'escape' || phase === 'hideout';
  const working = phase === 'execution' || phase === 'escape';

  const ping = (kind: PingKind) => {
    if (working) send({ type: 'ping', kind, room: view.exec?.room ?? null });
  };
  const phrase = (index: number) => () => {
    const id = QUICK_PHRASES[index];
    if (id && talking) say({ id, params: [] });
    else {
      const kind = QUICK_PINGS[index - QUICK_PHRASES.length];
      if (kind) ping(kind);
    }
  };

  const handlers: KeyHandlers = {
    quickPhrase1: phrase(0),
    quickPhrase2: phrase(1),
    quickPhrase3: phrase(2),
    quickPhrase4: phrase(3),
    quickPhrase5: phrase(4),
    quickPhrase6: phrase(5),
    quickPhrase7: phrase(6),
    quickPhrase8: phrase(7),
    quickPhrase9: phrase(8),
    ping: () => ping('here'),
    toggleChat: () => {
      if (talking) toggleChat();
    },
    toggleBlueprint: () => {
      const el = document.getElementById(PLAN_VIEW_ID);
      el?.focus();
      el?.scrollIntoView({ block: 'center' });
    },
    pause: onPause,
  };
  useKeyActions(handlers);
}
